import React, { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { errMsg, fmtDateTime, pretty } from "@/lib/residentApi";
import { inputCls } from "@/components/nds/windows/resident/ui";

const HIDE = new Set(["id", "resident_id", "created_at"]);

function RecordCard({ kind, rec, keep, setKeep }) {
  const [links, setLinks] = useState(null);
  useEffect(() => { apiClient.get(`/dupes/${kind}/linked/${encodeURIComponent(rec.id)}`).then((r) => setLinks(r.data.linked)).catch(() => setLinks({})); }, [kind, rec.id]);
  const isKeep = keep === rec.id;
  return (
    <div data-testid={`dupe-card-${rec.id}`} className={`flex-1 min-w-[240px] border rounded-sm p-3 space-y-2 transition-colors ${isKeep ? "border-emerald-400/50 bg-emerald-500/[0.04]" : "border-red-400/40 bg-red-500/[0.04]"}`}>
      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input type="radio" checked={isKeep} onChange={() => setKeep(rec.id)} data-testid={`dupe-keep-${rec.id}`} />
        <span className={isKeep ? "text-emerald-300 font-mono uppercase tracking-[0.14em]" : "text-red-300 font-mono uppercase tracking-[0.14em]"}>{isKeep ? "Keep this one" : "Delete this one"}</span>
      </label>
      <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-1 text-xs">
        {Object.entries(rec).filter(([k, v]) => !HIDE.has(k) && v !== null && v !== "").map(([k, v]) => (
          <React.Fragment key={k}><dt className="text-nex-muted">{pretty(k)}</dt><dd className="text-nex-silver break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</dd></React.Fragment>))}
        <dt className="text-nex-muted">Created</dt><dd className="text-nex-silver">{rec.created_at ? fmtDateTime(rec.created_at) : "—"}</dd>
        <dt className="text-nex-muted">Record id</dt><dd className="text-nex-muted font-mono text-[10px] break-all">{rec.id}</dd>
      </dl>
      <div className="border-t border-white/5 pt-2 text-[11px]" data-testid={`dupe-linked-${rec.id}`}>
        <div className="text-nex-muted mb-1">{isKeep ? "Linked records (kept)" : "Will be permanently removed:"}</div>
        {!links ? <span className="text-nex-muted">Counting…</span> : <ul className="space-y-0.5">{Object.entries(links).map(([c, n]) => <li key={c} className="flex justify-between gap-2 text-nex-silver"><span className="font-mono text-[10px] truncate">{c}</span><span>{n}</span></li>)}</ul>}
      </div>
    </div>
  );
}

export function DupeCompare({ kind, group, onDone, onClose }) {
  const [keep, setKeep] = useState(group.records[0].id);
  const [del, setDel] = useState(group.records[1].id);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const pair = group.records.filter((r) => r.id === keep || r.id === del);
  const choose = (id) => { if (id === del) setDel(keep); setKeep(id); };
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/dupes/delete", { kind, keep_id: keep, delete_id: del, confirm: typed });
      toast.success(`Deleted · ${Object.values(data.removed).reduce((a, b) => a + b, 0)} row(s) removed`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="dupe-compare" className="border border-nex-gold/30 rounded-sm p-3 space-y-3 bg-black/20">
      <div className="flex items-center gap-2 text-xs"><span className="text-nex-gold font-mono uppercase tracking-[0.14em]">Compare</span><span className="text-nex-muted">Match: {group.match}</span>
        <button type="button" onClick={onClose} data-testid="dupe-compare-close" className="ml-auto text-nex-muted hover:text-nex-silver underline">Close</button></div>
      {group.records.length > 2 && (
        <div className="flex flex-wrap gap-2 text-[11px] text-nex-muted">Delete which copy:
          {group.records.filter((r) => r.id !== keep).map((r) => <button key={r.id} type="button" onClick={() => setDel(r.id)} data-testid={`dupe-pick-${r.id}`} className={`px-2 py-0.5 border rounded-sm ${del === r.id ? "border-red-400/60 text-red-300" : "border-white/10"}`}>{(r.created_at || "").slice(0, 10) || r.id.slice(0, 8)}</button>)}</div>)}
      <div className="flex flex-wrap gap-3">{pair.map((r) => <RecordCard key={r.id} kind={kind} rec={r} keep={keep} setKeep={choose} />)}</div>
      <div className="flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
        <AlertTriangle className="w-4 h-4 text-red-300" /><span className="text-xs text-red-200">This is permanent, including any money rows linked to it. Type DELETE to confirm.</span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" data-testid="dupe-confirm-input" className={`${inputCls} !w-32 py-1.5`} />
        <button type="button" disabled={typed !== "DELETE" || busy} onClick={go} data-testid="dupe-delete-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-400/60 text-red-300 rounded-sm font-mono text-[10px] tracking-[0.16em] uppercase hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
          <Trash2 className="w-3.5 h-3.5" />{busy ? "Deleting…" : "Hard delete"}</button>
      </div>
    </div>
  );
}
